import type { MapType } from '@/features/map/map-components/map-layer-switch/map-layer-switch';

import React from 'react';

import { useIsIos, useIsMac } from '@/core/features/app/app-atoms';
import { getClientSideDeviceInfo } from '@/core/features/device/device-service';
import { useSetAtom } from '@/core/features/store/atom-store';
import { googleMapInstanceAtom, useGoogleMapInstance } from '@/features/map/google-map/google-map-state';
import { useMapState, useSetMapSize } from '@/features/map/map-container/map-state';
import { useIsMapBottomSheetInitiallyOpen } from '@/features/map/map-container/use-is-map-bottom-sheet-initially-open';

export type GoogleMapProps = {
    children?: React.ReactNode;
    initialPosition: { latitude: number | undefined; longitude: number | undefined; zoom: number | undefined };
    isBottomSheet?: boolean;
    mapType: MapType;
};

const GOOGLE_FONT_BASE_URL = 'https://fonts.googleapis.com/css';
const { deviceMemory, hasWebGlSupport, isDataSaverModeEnabled } = getClientSideDeviceInfo();

const GoogleMap_ = ({ children, initialPosition, isBottomSheet = false, mapType }: GoogleMapProps) => {
    const mapDivRef = React.useRef<HTMLDivElement>(null);
    const insertBeforeRef = React.useRef<Node['insertBefore'] | null>(null);
    const googleMapInstance = useGoogleMapInstance();
    const setGoogleMapInstance = useSetAtom(googleMapInstanceAtom);
    const isIos = useIsIos();
    const isMac = useIsMac();
    const hasVectorMapSupport = (deviceMemory >= 8 || isIos || isMac) && !isDataSaverModeEnabled && hasWebGlSupport;
    const mapState = useMapState();
    const isBottomSheetInitiallyOpen = useIsMapBottomSheetInitiallyOpen();

    const setMapSize = useSetMapSize();

    // If we come from a page that was larger than the window.innerHeight,
    // the map will be initially larger as well because it has height: 100%.
    // For the map position initialization, it is important to have the correct
    // map size, so we reject map sizes that are larger than the window.innerHeight
    // to prevent wrong calculations
    React.useEffect(() => {
        if (!mapDivRef.current) {
            return;
        }

        const mapSizeObserver = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.contentRect.height > window.innerHeight) {
                    return;
                }
                setMapSize({ height: entry.contentRect.height, width: entry.contentRect.width });
            });
        });

        mapSizeObserver.observe(mapDivRef.current);

        return () => {
            mapSizeObserver.disconnect();
        };
    }, [setMapSize]);

    // Prevent Google Maps from inserting unnecessary font links into the head
    React.useEffect(() => {
        const head = document.querySelector('head');

        if (!head) {
            return;
        }

        insertBeforeRef.current = head.insertBefore;

        // eslint-disable-next-line fp/no-mutation
        head.insertBefore = (node, child) => {
            const href = 'href' in node && typeof node.href === 'string' ? node.href : null;

            if (href && href.indexOf(GOOGLE_FONT_BASE_URL) === 0) {
                return node;
            }

            insertBeforeRef.current?.call(head, node, child);
            return node;
        };

        return () => {
            if (insertBeforeRef.current) {
                // eslint-disable-next-line fp/no-mutation
                head.insertBefore = insertBeforeRef.current;
            }
        };
    }, []);

    React.useEffect(() => {
        return () => {
            setGoogleMapInstance(null);
        };
    }, [setGoogleMapInstance]);

    React.useEffect(() => {
        if (!googleMapInstance) {
            return;
        }

        // In tempPosition states, the map might require larger bounds and smaller zoom levels than a
        // user is normally allowed to do
        const isStrict = !(
            isBottomSheetInitiallyOpen &&
            ['animating', 'initialization_finished', 'initializing', 'tempPosition'].includes(mapState)
        );

        googleMapInstance.setOptions({
            minZoom: isStrict ? 2 : 1,
            restriction: {
                latLngBounds: isStrict
                    ? { east: 180, north: 85, south: -85, west: -180 }
                    : { east: 180, north: 90, south: -90, west: -180 },
            },
        });
    }, [googleMapInstance, isBottomSheetInitiallyOpen, mapState]);

    React.useEffect(() => {
        if (!mapDivRef.current || googleMapInstance) {
            return;
        }

        const googleMap = new window.google.maps.Map(mapDivRef.current, {
            cameraControl: false,
            center: {
                lat: initialPosition?.latitude ?? 48,
                lng: initialPosition?.longitude ?? 8,
            },
            clickableIcons: false,
            fullscreenControl: false,
            gestureHandling: 'greedy',
            isFractionalZoomEnabled: hasVectorMapSupport,
            mapId: hasVectorMapSupport ? '8b0221f0afdb8dbe' : 'f373c617ac913536',
            mapTypeControl: false,
            mapTypeId: mapType,
            maxZoom: 19,
            minZoom: 2,
            renderingType: hasVectorMapSupport ? google.maps.RenderingType.VECTOR : google.maps.RenderingType.RASTER,
            restriction: { latLngBounds: { east: 180, north: 85, south: -85, west: -180 } },
            scaleControl: true,
            streetViewControl: false,
            zoom: initialPosition.zoom,
            zoomControl: false,
        });
        setGoogleMapInstance(googleMap);
    }, [mapDivRef, googleMapInstance, children, mapType, setGoogleMapInstance, hasVectorMapSupport, initialPosition]);

    return (
        <div
            className={`mapWrapper flex height-100 ${!isBottomSheet ? 'safe-area-bottom' : ''}`}
            data-qa-id={'qa-google-map-wrapper'}
        >
            <div className={'width-100 height-100'}>
                <div
                    className={'height-100 absolute-full grow-1'}
                    ref={mapDivRef}
                    /* eslint-disable-next-line no-inline-styles/no-inline-styles */
                    style={{ minHeight: 200 }}
                >
                    {children}
                </div>
            </div>
            <style jsx>{`
                .mapWrapper {
                    overflow: hidden;
                }
                .mapWrapper iframe + div {
                    border-color: transparent !important;
                }
                :global(.gm-style) {
                    font-family: inherit;
                }
            `}</style>
        </div>
    );
};

export default React.memo(GoogleMap_);
